import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, Link, Navigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { hasAdminAccess } from '../utils/permissions';

const adminNavLinks = [
  // 1. 핵심 개요
  { name: 'Dashboard', path: '/admin/dashboard' },
  
  // 2. 회원 관리 (People)
  { name: 'Application Management', path: '/admin/members' },
  { name: 'Staff Applications', path: '/admin/staff-applications' },
  { name: 'Member Info', path: '/admin/staff' },
  { name: 'Finance Management', path: '/admin/accounting' },
  
  // 3. 핵심 운영 (Operations)
  { name: 'Event Management', path: '/admin/events' },
  { name: 'Game Management', path: '/admin/games' },
  { name: 'Feedback', path: '/admin/feedback' },
  
  // 4. 자원 관리 (Assets)
  { name: 'Partnership Management', path: '/admin/partnerships' },
  { name: 'Materials & Rules', path: '/admin/resources' },
  
  // 5. 운영 도구 (Tools)
  { name: 'Action Plan', path: '/admin/tasks' },
  { name: 'Template Management', path: '/admin/templates' },
];

export default function AdminLayout() {
  const { user, logout } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 화면 크기 변경 감지하여 모바일 메뉴 상태 초기화
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 1200;
      setIsMobile(isMobileView);
      
      if (!isMobileView) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // 컴포넌트 마운트 시에도 한 번 체크
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // STAFF 이상의 권한을 가진 사용자만 Admin 페이지 접근 허용
  if (!user.isLoggedIn || !hasAdminAccess(user.role)) {
    return <Navigate to="/" replace />;
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Desktop Sidebar Navigation */}
      {!isMobile && (
        <aside className="w-64 bg-gray-800 text-white flex flex-col flex-shrink-0">
          <div className="p-6 text-2xl font-bold border-b border-gray-700 flex justify-between items-center">
            <span>SLAM Staff</span>
            <Link to="/" title="Go to Main Site" className="text-gray-400 hover:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </Link>
          </div>
          <nav className="flex-grow">
            {adminNavLinks.map(link => (
              <NavLink 
                key={link.path} 
                to={link.path} 
                className={({ isActive }) => `block px-6 py-3 transition-colors ${isActive ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
              >
                {link.name}
              </NavLink>
            ))}
          </nav>
        </aside>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-4">
            {/* Left Section: Mobile Menu Button + Title */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Mobile Menu Button */}
              {isMobile && (
                <button 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-gray-600 hover:text-gray-800 transition-colors flex-shrink-0 p-1"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              )}

              {/* Title */}
              {isMobile && (
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-base sm:text-lg font-bold text-gray-800 truncate">SLAM Staff</span>
                  <Link 
                    to="/" 
                    title="Go to Main Site" 
                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1"
                    aria-label="Go to main site"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </Link>
                </div>
              )}
            </div>

            {/* Right Section: User Info + Logout */}
            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
              <div className="flex flex-col items-end min-w-0">
                <span className="font-semibold text-xs sm:text-sm text-gray-900 truncate max-w-[120px] sm:max-w-none">
                  {isMobile ? user.email?.split('@')[0] : user.email}
                </span>
                <span className="text-xs text-gray-500 whitespace-nowrap">({user.role})</span>
              </div>
              <button 
                onClick={logout} 
                className="text-xs sm:text-sm text-red-600 hover:text-red-800 font-medium transition-colors px-2 sm:px-3 py-1.5 sm:py-2 rounded-md hover:bg-red-50 whitespace-nowrap flex-shrink-0"
              >
                Log Out
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Menu Panel */}
        {isMobile && isMobileMenuOpen && (
          <div className="bg-gray-800 text-white">
            <nav className="flex flex-col">
              {adminNavLinks.map(link => (
                <NavLink 
                  key={link.path} 
                  to={link.path} 
                  onClick={closeMobileMenu}
                  className={({ isActive }) => `block px-6 py-4 transition-colors ${isActive ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
                >
                  {link.name}
                </NavLink>
              ))}
            </nav>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}